import * as React from 'react';
import type { fetchHelpCenterAction } from 'epics/model/help-center';
import type { fetchPortalAction } from 'state/actions/portal';
import type { InjectedProps } from '@atlassian/help-center-common-component/with-path-params';
import { toNumber } from '@atlassian/help-center-common-util/number';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';

import type { Props as ModalProps } from './modal';
import { Modal } from './modal';
import { TRACK_EVENT_TYPE, OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';

export interface Props extends InjectedProps, ModalProps, WithAnalyticsEventsProps {
    fetchHelpCenter: typeof fetchHelpCenterAction;
    fetchPortal: typeof fetchPortalAction;
}

export class ModalWithFetcher extends React.Component<Props> {
    fetchHelpCenterOrPortalModel(portalId: string | undefined) {
        const portalIdAsNumber = toNumber(portalId);

        if (!!portalIdAsNumber) {
            this.props.fetchPortal({ id: portalIdAsNumber, expand: ['kbs', 'reqTypes', 'reqGroups', 'orderMapping'] });
        } else {
            this.props.fetchHelpCenter();
        }
    }

    componentDidMount() {
        const { term, shown, onChange, portalId } = this.props;
        if (shown && !!term && term.trim() !== '') {
            onChange(term, this.getAnalyticsData());
        }

        this.fetchHelpCenterOrPortalModel(portalId);
    }

    //Adding new portal and RT search success/failure analytics events as Operational event as mention here- https://hello.atlassian.net/wiki/spaces/TRELLO/pages/911176726/How%2BTo%2BReview%2Ba%2BGAS%2Bspec%2Band%2BPR
    getAnalyticsData = () => {
        const { createAnalyticsEvent, portalId } = this.props;
        return {
            analyticsSuccessEvent: {
                kb: createAnalyticsEvent?.({
                    action: portalId ? 'KBArticleSinglePortalSearch success' : 'KBArticleHomePageSearch success',
                    analyticsType: TRACK_EVENT_TYPE,
                    actionSubject: 'PortalSearch',
                }),
                portal: createAnalyticsEvent?.({
                    action: 'portalSearchSucceeded',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
                requestType: createAnalyticsEvent?.({
                    action: 'requestTypeSearchSucceeded',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
            },
            analyticsFailureEvent: {
                kb: createAnalyticsEvent?.({
                    action: portalId ? 'KBArticleSinglePortalSearch failed' : 'KBArticleHomePageSearch failed',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                }),
                portal: createAnalyticsEvent?.({
                    action: 'portalSearchFailed',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
                requestType: createAnalyticsEvent?.({
                    action: 'requestTypeSearchFailed',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
            },
        };
    };

    UNSAFE_componentWillUpdate(nextProps: Props) {
        // If the portal id changed (either between portals or from portal to help center)
        if (this.props.portalId !== nextProps.portalId) {
            this.fetchHelpCenterOrPortalModel(nextProps.portalId);
        }
    }

    render() {
        return <Modal {...this.props} />;
    }
}
